import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import PermissionsContainer from '../../../components/PermissionsContainer';
import { useToast } from '../../../contexts/ToastContext';
import {
  MAX_BULK, flagSet, fuelCardsService,
} from '../../../services/fuelCards.service';
import type {
  ActionOutcome, BlockReason, FuelCardListFilters, FuelCardRow, FuelCardsMeta,
} from '../../../services/fuelCards.service';
import FuelCardActionModal from './FuelCardActionModal';
import CardHistory from './CardHistory';
import { RATE_LIMITED_COMDATA, errMessage } from './errors';

/**
 * Fleet Management > Fuel Cards — the Comdata card mirror.
 *
 * The page's whole job is to be honest about what happened, because the endpoint
 * is. Every non-obvious rule here comes from a property of the API that a
 * cheerful UI would paper over:
 *
 * - The action modal COLLECTS INTENT ONLY. It hands back a payload and this page
 *   owns every outcome: `recorded: false` (Corpay may have acted but our
 *   audit/mirror write did not land), a plain failure, a bulk run that stopped
 *   early with an un-attempted remainder, and — DEFENSIVELY — `skipped`. That
 *   last one cannot reach this page today: `perform_card_action` sets it only
 *   for a non-manual source, and all three write routes here pass "manual" or
 *   "bulk", so it is the sweep's outcome. It is handled anyway, and tested
 *   BEFORE `ok` (which is *false* on a skip), because if the skip is ever
 *   widened to a route this page calls, a check that asks about `ok` first
 *   turns an honoured override into a red Comdata failure naming a card that
 *   nothing went wrong with. See the service module doc.
 *   The payload is passed straight through, never rebuilt: the modal already
 *   omits blank values rather than sending '', and rebuilding it here would
 *   reintroduce that.
 * - `status_after` is null on any failure, so a failure flips nothing and reloads
 *   instead. Only a success that was RECORDED updates a row in place.
 * - A bulk run is judged on `results.length + skipped.length` (the distinct
 *   tokens the service actually sent, after its own de-dupe), never on the raw
 *   selection — which would report a complete run as partial.
 * - The selection is capped at MAX_BULK *before* the modal opens. The modal
 *   renders whatever `cardCount` it is handed and enforces nothing, and the
 *   service rejects an over-cap list with a BARE `Error`, so without the cap an
 *   operator would confirm a run that cannot execute.
 * - A 403 is RBAC or a not-yet-deployed migration 433, never a Comdata fault.
 * - `Sync now` can honestly answer `records: 0` because another run holds the
 *   advisory lock, so its `warnings` are always shown.
 * - The mirror's flags are `0 | 1`; `last_action_success` is `0 | 1 | null` and
 *   its three states are exactly what this page exists to tell apart, so it gets
 *   a real three-way helper rather than `flagSet`, which collapses `0` and
 *   `null` the way a truthiness test would.
 */

type Filter = 'all' | 'active' | 'blocked' | 'override' | 'attention' | 'unjudged';

const CHIPS: { value: Filter; label: string; title: string }[] = [
  { value: 'all', label: 'All', title: 'Every card on file (terminated drivers with an already-blocked card are tidied away)' },
  { value: 'active', label: 'Active', title: "Cards Comdata reports as active ('A')" },
  { value: 'blocked', label: 'Blocked', title: 'Cards Comdata does not report as active — blocked, lost, or no status at all' },
  { value: 'override', label: 'Override', title: 'Cards held by a manual block/unblock, which the sweep skips' },
  { value: 'attention', label: 'Needs attention', title: 'Cards whose last attempt failed (one failure is enough)' },
  { value: 'unjudged', label: 'Unjudged', title: "Drivers with no resolvable unit row, so their status is unknown and the sweep cannot judge them" },
];

/**
 * The chips whose parameters make the endpoint lift its own terminated-card hide
 * (see `list_cards`: the hide applies only when `status_filter == 'all'` and
 * neither `attention` nor `unjudged` is asked for). On those views the
 * include-terminated toggle is a no-op, so it says so rather than pretending.
 */
const HIDE_LIFTED: Filter[] = ['active', 'blocked', 'attention', 'unjudged'];

/** Our own DATETIMEs. `comdata_status_date` is NOT one of these — Corpay mixes
 *  formats there, so it is rendered exactly as received. */
const formatStamp = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const trimmed = (value: string | null | undefined): string => (value ?? '').trim();

/** "Last, First" from the V1 join, which is null (not '') on an unlinked card. */
const driverLabel = (r: FuelCardRow): string => {
  const last = trimmed(r.driver_lastname);
  const first = trimmed(r.driver_firstname);
  if (last && first) return `${last}, ${first}`;
  return last || first || `Driver ${r.driver_recno}`;
};

type StatusTone = { label: string; title: string; className: string };

/**
 * Comdata's status code, compared case-INSENSITIVELY: the service preserves
 * whatever case Corpay sent. Anything that is not A or B — L lost, C clear,
 * D delete, X cancelled, or NULL for a word the sync did not recognise — is
 * "not active", with the raw code in the tooltip rather than guessed at.
 */
const statusTone = (status: string | null): StatusTone => {
  const code = trimmed(status).toUpperCase();
  if (code === 'A') {
    return {
      label: 'Active', title: 'Comdata reports this card as active (A)',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    };
  }
  if (code === 'B') {
    return {
      label: 'Blocked', title: 'Comdata reports this card as blocked (B)',
      className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    };
  }
  return {
    label: 'Not active',
    title: status === null
      ? 'Comdata status not reported — the sync did not recognise the status it was sent'
      : `Comdata status ${status} — not active, and not a code this page names`,
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  };
};

/**
 * `last_action_success` has THREE states and they matter: no action has ever
 * been recorded, one was and it failed, one was and it succeeded. `flagSet`
 * answers one question and collapses the first two, so this exists instead.
 */
type ActionState = 'never' | 'failed' | 'ok';
const lastActionState = (r: FuelCardRow): ActionState => {
  if (r.last_action_success === null || r.last_action === null) return 'never';
  return flagSet(r.last_action_success) ? 'ok' : 'failed';
};

/** The endpoint's whole Needs-attention rule. `>= 3` would hide a timed-out
 *  block, which stops at 1 and can never re-bump — the one case the sweep
 *  cannot resolve on its own. */
const needsAttention = (r: FuelCardRow): boolean => r.consecutive_failures >= 1;

const cardLabel = (r: FuelCardRow): string => {
  const four = trimmed(r.last_four);
  // Every row has a driver now — the driver IS the key — so the name always
  // leads. The last four are a confirmation, not the identifier they were when
  // a card could arrive unattached to anyone.
  const bits = [driverLabel(r), four ? `****${four}` : 'no card number on file'];
  const unit = trimmed(r.driver_unitnumber) || trimmed(r.card_unit_number);
  if (unit) bits.push(`Unit ${unit}`);
  return bits.join(' · ');
};

const chipClass = (active: boolean): string => `rounded-full px-3 py-1 text-xs font-medium transition-colors ${
  active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-label hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
}`;

const pill = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

const FuelCards: React.FC = () => {
  const { showToast } = useToast();

  const [rows, setRows] = useState<FuelCardRow[]>([]);
  const [meta, setMeta] = useState<FuelCardsMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [includeTerminated, setIncludeTerminated] = useState(false);
  /** Bumped after every block/unblock run, to clear the acted-on selection. */
  const [runCount, setRunCount] = useState(0);
  const [selected, setSelected] = useState<FuelCardRow[]>([]);
  const [busy, setBusy] = useState<Set<number>>(new Set());
  const [modal, setModal] = useState<{ action: 'block' | 'unblock'; tokens: number[]; label: string } | null>(null);
  const [modalBusy, setModalBusy] = useState(false);
  const [reasons, setReasons] = useState<BlockReason[]>([]);
  const [reasonSource, setReasonSource] = useState<'comdata' | 'fallback' | null>(null);
  const [syncing, setSyncing] = useState(false);

  const writesEnabled = meta?.writes_enabled === true;
  // /sync is gated on the integration being ON and CONFIGURED — not on the write
  // switch, which only stops block/unblock. Refusing a mirror refresh because
  // writes are paused would leave the operator unable to see why.
  const canSync = meta?.enabled === true && meta?.configured === true;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const filters: FuelCardListFilters = {};
    if (filter === 'active' || filter === 'blocked') filters.status = filter;
    if (filter === 'override') filters.override = true;
    if (filter === 'attention') filters.attention = true;
    if (filter === 'unjudged') filters.unjudged = true;
    if (includeTerminated) filters.include_terminated = true;
    try {
      const res = await fuelCardsService.list(filters);
      setRows(res.data ?? []);
      setMeta(res.meta ?? null);
    } catch (e) {
      setError(errMessage(e, 'Could not load the fuel-card mirror.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, includeTerminated]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let live = true;
    fuelCardsService.blockReasons()
      .then((res) => {
        if (!live) return;
        setReasons(res.data ?? []);
        setReasonSource(res.source);
      })
      .catch(() => {
        // The endpoint already falls back to a static list rather than failing,
        // so reaching here means the request itself did not land. The modal's
        // own copy covers an empty list; nothing to say twice.
        if (live) setReasons([]);
      });
    return () => { live = false; };
  }, []);

  const markBusy = (tokens: number[], on: boolean) => {
    setBusy((prev) => {
      const next = new Set(prev);
      tokens.forEach((t) => (on ? next.add(t) : next.delete(t)));
      return next;
    });
  };

  /** A recorded success is the only outcome that may touch a row in place. */
  const applyOutcome = useCallback((o: ActionOutcome) => {
    if (!o.ok || o.skipped || !o.recorded || !o.status_after) return;
    setRows((prev) => prev.map((r) => (r.driver_recno === o.driver_recno
      // A manual/bulk action also takes the card off the sweep (actions.py sets
      // override_flag with who/when); who and when arrive on the next read.
      ? { ...r, comdata_status: o.status_after, override_flag: 1 as const }
      : r)));
  }, []);

  const labelFor = useCallback((recno: number): string => {
    const r = rows.find((x) => x.driver_recno === recno);
    return r ? cardLabel(r) : `Driver ${recno}`;
  }, [rows]);

  const runSingle = async (
    action: 'block' | 'unblock', token: number, payload: { lock_reason?: string; note?: string },
  ) => {
    // The payload is passed through untouched — see the header comment.
    const o = action === 'block'
      ? await fuelCardsService.block(token, payload)
      : await fuelCardsService.unblock(token, payload);
    // `skipped` FIRST: a skip comes back with ok === false, so any check that
    // asks about `ok` before `skipped` would call an override skip a Comdata
    // failure. Defensive — no route this page calls can return a skip today;
    // see the header comment.
    if (o.skipped) {
      showToast({
        type: 'warning',
        message: `${labelFor(token)}: nothing was sent to Comdata — an operator override is in force. `
          + 'Clear the override first if you meant to take this card back.',
      });
      return;
    }
    if (!o.ok) {
      showToast({
        type: 'error',
        message: `${labelFor(token)}: ${o.error ?? o.response_message ?? 'Comdata refused the request.'}`,
      });
      // A failure carries no status at all, so re-read rather than guess.
      load();
      return;
    }
    if (!o.recorded) {
      showToast({
        type: 'warning',
        message: `${labelFor(token)}: ${action === 'block' ? 'blocked' : 'unblocked'} at Comdata, but we `
          + 'could not record it. Refresh after the next sync to confirm where the card ended up.',
      });
      // Deliberately NO reload and NO flip: the write that failed IS the mirror
      // write, so re-reading would only show the same stale row while implying
      // it had been confirmed. The next sync is what settles this card.
      return;
    }
    applyOutcome(o);
    showToast({
      type: 'success',
      message: `${labelFor(token)}: ${action === 'block' ? 'blocked' : 'unblocked'}.`
        + (o.response_message ? ` Comdata said: ${o.response_message}.` : ''),
    });
  };

  const runBulk = async (
    action: 'block' | 'unblock', tokens: number[], payload: { lock_reason?: string; note?: string },
  ) => {
    // De-duped here as well as in the service, because this count is what the
    // report is measured against and it has to be the number actually sent.
    const distinct = Array.from(new Set(tokens));
    const res = await fuelCardsService.bulk({ driver_recnos: distinct, action, ...payload });
    res.results.forEach(applyOutcome);

    const verb = action === 'block' ? 'Blocked' : 'Unblocked';
    const done = res.results.filter((o) => o.ok && o.recorded).length;
    const unrecorded = res.results.filter((o) => o.ok && !o.recorded).length;
    const overridden = res.results.filter((o) => o.skipped).length;
    const failures = res.results.filter((o) => !o.ok && !o.skipped);
    const notAttempted = res.skipped.length;
    // The service guarantees results + skipped === the tokens it sent, so this
    // is the arithmetic that separates a complete run from a partial one.
    const accounted = res.results.length + notAttempted;
    const complete = !res.stopped_early && notAttempted === 0 && failures.length === 0
      && overridden === 0 && unrecorded === 0 && accounted === distinct.length;

    // The lead counts every card Comdata actually moved, recorded or not. Both
    // halves of that matter: if our database is down for a whole run, all 25
    // cards move and none are recorded, and a lead of "Blocked 0 of 25" is the
    // sentence that gets read — flatly contradicted by the next one. The
    // unrecorded caveat carries the distinction instead.
    const moved = done + unrecorded;
    const parts = [`${verb} ${moved} of ${distinct.length} card${distinct.length === 1 ? '' : 's'}.`];
    if (unrecorded) {
      parts.push(`${unrecorded} of those moved at Comdata but we could not record it — `
        + 'the page cannot show their new state until the next sync.');
    }
    if (overridden) parts.push(`${overridden} skipped: an operator override is in force.`);
    if (notAttempted) {
      parts.push(`${notAttempted} not attempted${res.stop_reason ? ` — ${res.stop_reason}` : ''}. `
        + 'Select those cards again to finish the run.');
    } else if (res.stopped_early && res.stop_reason) {
      parts.push(`The run stopped early — ${res.stop_reason}.`);
    }
    // `<`, not `!==`: the backend cannot return more than it was sent, but if it
    // ever did, `!==` would render "-2 card(s) came back unaccounted for".
    if (accounted < distinct.length) {
      parts.push(`${distinct.length - accounted} card(s) came back unaccounted for; reload to see their real state.`);
    }
    showToast({ type: complete ? 'success' : 'warning', message: parts.join(' '), duration: 12000 });

    // Re-read only where the row state is genuinely unknown to us. An unrecorded
    // write and an override skip are NOT such cases: in both the mirror is
    // exactly as it was, so a reload would show a stale row as if it were fresh.
    const mustReread = failures.length > 0 || notAttempted > 0 || res.stopped_early
      || accounted !== distinct.length;

    if (failures.length) {
      showToast({
        type: 'error',
        message: `${failures.length} card${failures.length === 1 ? '' : 's'} failed: `
          + failures.map((f) => `${labelFor(f.driver_recno)} — ${f.error ?? f.response_message ?? 'no reason given'}`)
            .join('; '),
        duration: 15000,
      });
    }
    if (mustReread) load();
  };

  const handleConfirm = async (payload: { lock_reason?: string; note?: string }) => {
    if (!modal || modalBusy) return;
    const { action, tokens } = modal;
    setModalBusy(true);
    markBusy(tokens, true);
    try {
      // One card goes down the single-card route whichever way it was picked: its
      // outcome is the same dataclass, without a bulk envelope to unwrap.
      if (tokens.length === 1) await runSingle(action, tokens[0], payload);
      else await runBulk(action, tokens, payload);
    } catch (e) {
      // Only the SINGLE-card path can surface Corpay's own 429: `_run_action` is
      // the one place that re-raises it as an HTTPException. `bulk` catches it,
      // records "rate limited (429)" per card and answers 200, so a 429 thrown
      // out of a bulk run came from the gateway, not from Comdata.
      showToast({
        type: 'error',
        message: errMessage(e, undefined, undefined,
          tokens.length === 1 ? RATE_LIMITED_COMDATA : undefined),
      });
      load();
    } finally {
      markBusy(tokens, false);
      setModalBusy(false);
      setModal(null);
      // Drop the selection once it has been acted on. Leaving it checked leaves
      // "Block 25" armed on the same 25 cards, and `perform_card_action` does
      // not short-circuit a card that is already in the target state — so one
      // stray click sends 25 more Corpay calls and writes 25 more audit rows.
      //
      // Only when the run actually touched the selection, though: the reset key
      // clears ALL of it, so bumping unconditionally would throw away a
      // multi-card selection an operator was still building because they used
      // one row's own Block button in the middle of building it.
      // `selected` is read as of the CONFIRM CLICK, not as of when the run ends:
      // this closure is not memoized, so it captures the value the operator was
      // looking at. That is the behaviour to want — a reload part-way through a
      // long bulk run cannot prune the selection out from under the test and
      // leave the bar armed — but it is a property of the closure, so say so.
      const inSelection = new Set(selected.map((r) => r.driver_recno));
      if (tokens.some((t) => inSelection.has(t))) setRunCount((n) => n + 1);
    }
  };

  // Link and unlink are gone with the matching layer. A card belongs to whoever
  // holds it in V1, so a wrong card is a wrong card NUMBER, corrected on the
  // driver record in the safety app rather than re-pointed here.

  const onClearOverride = useCallback(async (r: FuelCardRow) => {
    markBusy([r.driver_recno], true);
    try {
      await fuelCardsService.clearOverride(r.driver_recno);
      showToast({
        type: 'success',
        message: `${cardLabel(r)}: override cleared — the sweep decides this card again.`,
      });
      load();
    } catch (e) {
      showToast({ type: 'error', message: `${cardLabel(r)}: ${errMessage(e, 'Could not clear the override.')}` });
    } finally {
      markBusy([r.driver_recno], false);
    }
  }, [load, showToast]);

  const onSync = async () => {
    setSyncing(true);
    try {
      const res = await fuelCardsService.syncNow();
      const summary = `Sync finished: ${res.records} card${res.records === 1 ? '' : 's'} read, `
        + `${res.inserted} new, ${res.updated} updated, ${res.status_changed} status change(s), `
        + `${res.errors} error(s).`;
      // A zero-record run is usually the scheduled tick holding the advisory
      // lock, not an empty mirror — so the warnings are never dropped.
      const warnings = res.warnings ?? [];
      showToast({
        type: warnings.length ? 'warning' : 'success',
        message: warnings.length ? `${summary} ${warnings.join(' ')}` : summary,
        duration: 12000,
      });
      load();
    } catch (e) {
      // No Comdata wording here: `run_card_sync` catches a Corpay rate limit and
      // returns it as a `warnings` entry on a 200, so a 429 thrown out of /sync
      // is the gateway.
      showToast({ type: 'error', message: errMessage(e, 'Could not run the sync.') });
    } finally {
      setSyncing(false);
    }
  };

  const openAction = useCallback((action: 'block' | 'unblock', tokens: number[], label: string) => {
    setModal({ action, tokens, label });
  }, []);

  const changeFilter = (next: Filter) => {
    // TanTable clears its own checkboxes off `selectionResetKey`; this keeps our
    // copy of the selection from outliving them.
    setSelected([]);
    setFilter(next);
  };

  const columns = useMemo<ColumnDef<FuelCardRow>[]>(() => [
    {
      id: 'driver',
      header: 'Driver',
      accessorFn: (r) => (r.driver_recno === null ? 'zzz unlinked' : driverLabel(r)),
      cell: ({ row }) => {
        const r = row.original;
        if (r.driver_recno === null) {
          return (
            <span data-testid={`driver-${r.driver_recno}`} className={`${pill} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}>
              Unlinked
            </span>
          );
        }
        const unit = trimmed(r.driver_unitnumber);
        return (
          <span data-testid={`driver-${r.driver_recno}`} className="flex items-center gap-2">
            <span className="text-body">{driverLabel(r)}</span>
            {unit && (
              <span className={`${pill} bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200`}>
                {unit}
              </span>
            )}
            {/* Linked, but no unit row resolved — the sweep can judge neither way. */}
            {r.unit_status === null && (
              <span className={`${pill} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200`} title="No unit row resolves for this driver, so their status is unknown">
                unjudged
              </span>
            )}
            {trimmed(r.unit_status).toUpperCase() === 'TERMINATED' && (
              <span className={`${pill} bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200`}>
                terminated
              </span>
            )}
          </span>
        );
      },
      size: 230,
    },
    {
      id: 'card',
      header: 'Card',
      accessorFn: (r) => trimmed(r.last_four),
      cell: ({ row }) => {
        const r = row.original;
        const four = trimmed(r.last_four);
        // The name Comdata holds on the card, which is worth seeing when it
        // disagrees with the driver record — that disagreement is the signal
        // that a card number was typed against the wrong driver.
        const onCard = [trimmed(r.card_last_name), trimmed(r.card_first_name)]
          .filter(Boolean).join(', ');
        return (
          <span className="text-body" title={onCard ? `Comdata has: ${onCard}` : undefined}>
            {four ? `****${four}` : '—'}
          </span>
        );
      },
      size: 110,
    },
    {
      id: 'fueltype',
      header: 'Fuel type',
      accessorFn: (r) => trimmed(r.driver_fueltype),
      cell: ({ row }) => trimmed(row.original.driver_fueltype) || <span className="text-gray-400">—</span>,
      size: 100,
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (r) => statusTone(r.comdata_status).label,
      cell: ({ row }) => {
        const tone = statusTone(row.original.comdata_status);
        return (
          <span
            data-testid={`status-${row.original.driver_recno}`}
            className={`${pill} ${tone.className}`}
            title={tone.title}
          >
            {tone.label}
          </span>
        );
      },
      size: 110,
    },
    {
      id: 'status_date',
      header: 'Status date',
      // Rendered raw: Corpay mixes date formats in this field, so parsing it
      // would show a wrong date rather than an unfamiliar one.
      accessorFn: (r) => trimmed(r.comdata_status_date),
      cell: ({ row }) => trimmed(row.original.comdata_status_date) || <span className="text-gray-400">—</span>,
      size: 130,
    },
    {
      id: 'control',
      header: 'Control',
      accessorFn: (r) => (flagSet(r.override_flag) ? 'Override' : 'Automatic'),
      cell: ({ row }) => {
        const r = row.original;
        const held = flagSet(r.override_flag);
        const who = trimmed(r.override_by);
        const title = held
          ? (who || r.override_at
            ? `Manual override by ${who || 'unknown'}${r.override_at ? ` at ${formatStamp(r.override_at)}` : ''}`
              + (trimmed(r.override_reason) ? ` — ${trimmed(r.override_reason)}` : '')
            : 'A manual override is in force — who and when will show after the next refresh')
          : 'The nightly sweep decides this card';
        return (
          <span
            data-testid={`control-${r.driver_recno}`}
            className={`${pill} ${held
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}
            title={title}
          >
            {held ? 'Override' : 'Automatic'}
          </span>
        );
      },
      size: 110,
    },
    {
      id: 'last_action',
      header: 'Last action',
      accessorFn: (r) => (lastActionState(r) === 'never' ? '' : `${r.last_action} ${r.last_action_source ?? ''}`),
      cell: ({ row }) => {
        const r = row.original;
        const state = lastActionState(r);
        if (state === 'never') {
          return <span data-testid={`last-action-${r.driver_recno}`} className="text-gray-400">—</span>;
        }
        const bits = [r.last_action, r.last_action_source, trimmed(r.last_action_by) || null,
          r.last_action_at ? formatStamp(r.last_action_at) : null].filter(Boolean);
        return (
          <span
            data-testid={`last-action-${r.driver_recno}`}
            className={state === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-body'}
            title={state === 'failed' ? 'This attempt failed' : undefined}
          >
            {bits.join(' · ')}
          </span>
        );
      },
      size: 260,
    },
    {
      id: 'attention',
      header: 'Attention',
      accessorFn: (r) => (needsAttention(r) ? 'Needs attention' : ''),
      cell: ({ row }) => {
        const r = row.original;
        if (!needsAttention(r)) return null;
        return (
          <span
            data-testid={`attention-${r.driver_recno}`}
            className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"
            title={`${r.consecutive_failures} consecutive failure(s)`
              + `${r.last_error ? `: ${r.last_error}` : ''}`}
          >
            <ExclamationTriangleIcon className="size-4" aria-hidden="true" />
            <span className="text-xs">{r.consecutive_failures}</span>
          </span>
        );
      },
      size: 90,
    },
    {
      id: 'last_synced',
      header: 'Last synced',
      accessorFn: (r) => trimmed(r.last_synced_at),
      cell: ({ row }) => <span className="text-muted">{formatStamp(row.original.last_synced_at)}</span>,
      size: 160,
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const r = row.original;
        const isBusy = busy.has(r.driver_recno);
        const active = statusTone(r.comdata_status).label === 'Active';
        return (
          <div className="flex items-center gap-2">
            <PermissionsContainer
              componentId="fuel-cards-toggle"
              componentName="Fuel Cards -- Block / Unblock buttons"
              defaultHidden
              showHandle={false}
            >
              <button
                type="button"
                onClick={() => openAction(active ? 'block' : 'unblock', [r.driver_recno], cardLabel(r))}
                disabled={isBusy || !writesEnabled}
                title={writesEnabled ? undefined : meta?.writes_disabled_reason ?? 'Comdata writes are unavailable'}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${active
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50'
                  : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50'}`}
              >
                {active ? 'Block' : 'Unblock'}
              </button>
            </PermissionsContainer>

            <PermissionsContainer
              componentId="fuel-cards-link"
              componentName="Fuel Cards -- Clear override"
              defaultHidden
              showHandle={false}
            >
              <div className="flex items-center gap-2">
                {/* Clearing an override writes only our own table, so it is NOT
                    gated on the Comdata write switch. Link and unlink are gone:
                    a card belongs to whoever holds it in V1, so a wrong card is
                    a wrong card NUMBER, fixed on the driver record. */}
                {flagSet(r.override_flag) && (
                  <button
                    type="button"
                    onClick={() => onClearOverride(r)}
                    disabled={isBusy}
                    className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-200 disabled:opacity-50 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
                  >
                    Clear override
                  </button>
                )}
              </div>
            </PermissionsContainer>
          </div>
        );
      },
      size: 220,
    },
  ], [busy, writesEnabled, meta, openAction, onClearOverride]);

  const capped = selected.slice(0, MAX_BULK);
  const overCap = selected.length > MAX_BULK;
  const bulkLabel = `${capped.length} card${capped.length === 1 ? '' : 's'}`;
  const hideLifted = HIDE_LIFTED.includes(filter);

  return (
    <div className="space-y-3 p-4 pb-24">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-heading">Fuel Cards</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Comdata cards for GWI drivers • Last synced: {formatStamp(meta?.last_synced_at ?? null)}
            {!!meta?.retired_count && (
              // Stated, not filterable: these cards no longer exist at Comdata,
              // so there is nothing to do about one. The count is here so the row
              // total reads as deliberate rather than as missing data.
              <> • {meta.retired_count.toLocaleString()} retired (no longer at Comdata, hidden)</>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-label hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onSync}
            disabled={syncing || !canSync}
            title={canSync ? 'Read the card mirror from Comdata now' : 'The Comdata integration is off or not configured here'}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
      </div>

      {/* Banner order: the environment switch, then the mode, then the writes gate. */}
      {meta && !meta.enabled && (
        <div className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
          Comdata integration is disabled in this environment.
        </div>
      )}
      {/* `=== 'fixture'`, not truthiness: `mode` is NULL on a configuration error
          and must never be rendered raw. */}
      {meta?.mode === 'fixture' && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <span>Fixture mode: cards and toggles are simulated, nothing reaches Comdata.</span>
        </div>
      )}
      {/* Gated on the FLAG only, never on the reason. The reason is normally
          non-empty, but `list_meta` builds one as `reason or str(exc)` and an
          exception can stringify to "" — and the buttons go dead off the flag
          regardless. Requiring the reason here would take the explanation away
          in exactly the case where it is hardest to guess. */}
      {meta && !meta.writes_enabled && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <span>{trimmed(meta.writes_disabled_reason) || 'Comdata writes are switched off. Blocking and unblocking are unavailable.'}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {CHIPS.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => changeFilter(c.value)}
            title={c.title}
            aria-pressed={filter === c.value}
            className={chipClass(filter === c.value)}
          >
            {c.label}
          </button>
        ))}
        <label
          htmlFor="fuel-cards-include-terminated"
          className="ml-2 flex items-center gap-1.5 text-xs text-label"
          title={hideLifted
            ? 'This view already includes terminated drivers’ cards'
            : 'By default a terminated driver whose card is already blocked is tidied away'}
        >
          <input
            id="fuel-cards-include-terminated"
            type="checkbox"
            checked={includeTerminated || hideLifted}
            disabled={hideLifted}
            onChange={(e) => setIncludeTerminated(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Include terminated drivers’ cards
        </label>
        {reasonSource && (
          <span className="ml-auto text-xs text-muted">
            {reasonSource === 'comdata'
              ? 'Block reasons: Comdata’s live list.'
              : 'Block reasons: the standard list — Comdata’s own list was unavailable.'}
          </span>
        )}
      </div>

      {/* Selection bar (TrackingLinks pattern). */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
          <span className="text-sm text-blue-900 dark:text-blue-200">
            {selected.length} selected
          </span>
          {overCap && (
            <span className="text-sm text-blue-800 dark:text-blue-300">
              Only the first {MAX_BULK} will run — that is the per-run cap. Repeat for the rest.
            </span>
          )}
          <PermissionsContainer
            componentId="fuel-cards-toggle"
            componentName="Fuel Cards -- Block / Unblock buttons"
            defaultHidden
            showHandle={false}
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openAction('block', capped.map((r) => r.driver_recno), bulkLabel)}
                disabled={!writesEnabled || capped.length === 0}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Block {capped.length}
              </button>
              <button
                type="button"
                onClick={() => openAction('unblock', capped.map((r) => r.driver_recno), bulkLabel)}
                disabled={!writesEnabled || capped.length === 0}
                className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Unblock {capped.length}
              </button>
            </div>
          </PermissionsContainer>
        </div>
      )}

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        errorMessage={error}
        onRetry={load}
        settingsKey="fleet-fuel-cards"
        cardTitle="Fuel Cards"
        searchPlaceholder="Search cards, drivers, units…"
        enableSearch
        enableColumnFilters
        enableRowSelection
        onSelectionChange={setSelected}
        getRowId={(r: FuelCardRow) => String(r.driver_recno)}
        selectionResetKey={`${filter}:${runCount}`}
        enableExcelExport
        disableVirtualization
        lockedColumns={[{ id: 'actions', side: 'right' }]}
        defaultSorting={[{ id: 'driver', desc: false }]}
        renderExpandedContent={(r: FuelCardRow) => <CardHistory driverRecno={r.driver_recno} />}
      />

      <FuelCardActionModal
        open={modal !== null}
        action={modal?.action ?? 'block'}
        cardCount={modal?.tokens.length ?? 0}
        cardLabel={modal?.label ?? ''}
        reasons={reasons}
        loading={modalBusy}
        writesDisabledReason={meta?.writes_enabled === false ? meta.writes_disabled_reason : null}
        onConfirm={handleConfirm}
        onCancel={() => { if (!modalBusy) setModal(null); }}
      />
    </div>
  );
};

export default FuelCards;
